import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Eye, LoaderCircle, MoonStar, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINES = [
    "SOMEWHERE IN THIS WORLD,",
    "YOUR OTHER LIFE MAY ALREADY BE",
    "HAPPENING TO SOMEONE ELSE.",
];

const LONGINGS = [
    "Is she the one living my life?",
    "Has she taken what was mine?",
    "Will I ever get it back?",
    "Does she know what she did?",
];

const loadSaved = () => {
    try {
        return JSON.parse(localStorage.getItem("ds_oracle") || "null");
    } catch {
        return null;
    }
};

export const Hero = ({ onBegin }) => {
    const [readings, setReadings] = useState(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 50, damping: 16 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 50, damping: 16 });
    const { scrollY } = useScroll();
    const yDrift = useTransform(scrollY, [0, 700], [0, 140]);
    const fade = useTransform(scrollY, [0, 550], [1, 0.1]);

    const saved = loadSaved();
    const [visitorId] = useState(() => {
        let v = localStorage.getItem("ds_oracle_vid");
        if (!v) {
            v = crypto.randomUUID();
            localStorage.setItem("ds_oracle_vid", v);
        }
        return v;
    });
    const [name, setName] = useState(saved?.name || "");
    const [email, setEmail] = useState(saved?.email || "");
    const [optIn, setOptIn] = useState(saved?.optIn ?? true);
    const [identified, setIdentified] = useState(!!saved?.email);
    const [question, setQuestion] = useState("");
    const [answers, setAnswers] = useState(saved?.answers || []);
    const [remaining, setRemaining] = useState(saved?.remaining ?? 3);
    const [gazing, setGazing] = useState(false);

    useEffect(() => {
        localStorage.setItem("ds_oracle", JSON.stringify({ name, email, optIn, remaining, answers }));
    }, [name, email, optIn, remaining, answers]);

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    useEffect(() => {
        axios.get(`${API}/stats`).then((r) => setReadings(r.data.readings)).catch(() => {});
    }, []);

    useEffect(() => {
        if (readings == null) return;
        let t;
        const tick = () => {
            setReadings((r) => r + 1 + Math.floor(Math.random() * 3));
            t = setTimeout(tick, 12000 + Math.random() * 18000);
        };
        t = setTimeout(tick, 8000);
        return () => clearTimeout(t);
    }, [readings != null]);

    const begin = (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) return;
        setIdentified(true);
    };

    const ask = async (q) => {
        const questionText = (q || question).trim();
        if (!questionText || gazing || remaining <= 0) return;
        setGazing(true);
        setQuestion("");
        try {
            const res = await axios.post(`${API}/oracle/ask`, {
                visitor_id: visitorId,
                name: name.trim(),
                email: email.trim(),
                question: questionText,
                marketing_opt_in: optIn,
            });
            if (res.data.locked && !res.data.answer) {
                setRemaining(0);
            } else {
                setAnswers((a) => [{ q: questionText, a: res.data.answer }, ...a]);
                setRemaining(res.data.remaining);
            }
        } catch (err) {
            if (err?.response?.status === 403) {
                toast.error("The mirror is closed to this path.");
            } else {
                toast.error("The mirror clouded. Ask again in a breath.");
            }
        } finally {
            setGazing(false);
        }
    };

    const locked = remaining <= 0;
    const latest = answers[0] || null;

    return (
        <section
            id="hero"
            onMouseMove={onMove}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-5 pb-24 pt-28 text-center"
        >
            <motion.div style={{ y: yDrift }} className="absolute inset-0">
                <div className="nebula-blob left-[-10%] top-[-15%] h-[34rem] w-[34rem] bg-[#5a189a]/35" />
                <div className="nebula-blob bottom-[-20%] right-[-10%] h-[38rem] w-[38rem] bg-[#2e0b53]/50" />
                <div className="nebula-blob left-[30%] top-[40%] h-[26rem] w-[26rem] bg-[#7b2cbf]/20" />
            </motion.div>

            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 1 }}
                className="eyebrow relative mb-6"
            >
                A reading of the life that mirrors yours
            </motion.p>

            <h1
                data-testid="hero-headline"
                className="text-glow-lilac relative max-w-5xl font-display text-3xl font-bold leading-snug tracking-wide text-[#f8f7ff] sm:text-4xl lg:text-5xl lg:leading-snug"
            >
                {LINES.map((line, i) => (
                    <span key={line} className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: "115%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 1.1, delay: 0.7 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                        >
                            {line}
                        </motion.span>
                    </span>
                ))}
            </h1>

            {/* The mirror itself — sisters wrapped around the rim, the free reading inside */}
            <div id="oracle" data-testid="oracle-section" className="relative mt-12 scroll-mt-24 [perspective:1200px]">
                <motion.div
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.6, delay: 1.2, ease: "easeOut" }}
                >
                    <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
                        <div className="portal-ring absolute -inset-6 animate-spin-slower rounded-full sm:-inset-10" />
                        <div
                            className={`absolute -inset-4 rounded-full blur-2xl transition-all duration-1000 sm:-inset-8 ${
                                gazing ? "animate-pulse-soft bg-[#c77dff]/45" : "bg-[#7b2cbf]/25"
                            }`}
                        />

                        <div className="relative h-[21rem] w-[21rem] sm:h-[27rem] sm:w-[27rem] lg:h-[31rem] lg:w-[31rem]">
                            <img
                                src="/assets/mirror-frame.png"
                                alt="The two Dichotomy Sisters — one pearl white, one shadow violet — wrapped around the rim of Mara's mirror"
                                data-testid="hero-logo-image"
                                className="animate-float-y absolute inset-0 h-full w-full rounded-full drop-shadow-[0_0_45px_rgba(168,85,247,0.55)]"
                            />

                            {/* the dark glass — where the mirror answers */}
                            <div
                                data-testid="mirror-glass"
                                className={`absolute left-1/2 top-[49%] flex h-[55%] w-[55%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center overflow-hidden rounded-full border bg-[#0b031e]/90 px-9 text-center transition-[border-color,box-shadow] duration-700 sm:px-12 ${
                                    gazing
                                        ? "border-[#e0aaff]/70 shadow-[0_0_60px_rgba(224,170,255,0.5)]"
                                        : "border-purple-400/25 shadow-[inset_0_0_40px_rgba(123,44,191,0.35)]"
                                }`}
                            >
                                <div className="mirror-sheen pointer-events-none absolute inset-0 rounded-full" />
                                <AnimatePresence mode="wait">
                                    {gazing ? (
                                        <motion.p
                                            key="gazing"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="font-cinzel text-[10px] tracking-[0.3em] text-[#e0aaff]/80"
                                        >
                                            THE MIRROR IS LOOKING BACK…
                                        </motion.p>
                                    ) : latest ? (
                                        <motion.div
                                            key={`answer-${latest.q}`}
                                            initial={{ opacity: 0, y: 14 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.9 }}
                                            data-testid="oracle-answer-card"
                                            className="h-full w-full overflow-y-auto px-2 py-8"
                                        >
                                            <p className="font-cormorant text-[11px] italic text-[#c77dff]/70">“{latest.q}”</p>
                                            <p className="mt-2 font-cormorant text-sm italic leading-relaxed text-[#f8f7ff]/95">
                                                {latest.a}
                                            </p>
                                            <p className="mt-2 font-cinzel text-[8px] tracking-[0.3em] text-[#c77dff]/50">
                                                — THE MIRROR, THROUGH MARA
                                            </p>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="idle"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="px-2"
                                        >
                                            <Sparkles className="mx-auto h-4 w-4 animate-pulse-soft text-[#c77dff]" />
                                            <p className="mt-3 font-cinzel text-xs tracking-[0.25em] text-[#e0aaff]">
                                                ASK MARA'S MIRROR
                                            </p>
                                            <p className="mt-2 font-cormorant text-sm italic leading-snug text-[#e0aaff]/70">
                                                three answers, freely given — whisper below
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            {/* the ask bar */}
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="relative mt-10 w-full max-w-xl"
            >
                {!identified ? (
                    <form onSubmit={begin} data-testid="oracle-identify-form" className="glass-card p-5">
                        <p className="eyebrow mb-4">Tell the mirror who asks</p>
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <input
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid="oracle-name-input"
                                placeholder="Your first name"
                                className="flex-1 rounded-full border border-purple-500/30 bg-[#0b031e]/70 px-5 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 focus:border-[#c77dff]/70 focus:outline-none"
                            />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="oracle-email-input"
                                placeholder="Your email"
                                className="flex-1 rounded-full border border-purple-500/30 bg-[#0b031e]/70 px-5 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 focus:border-[#c77dff]/70 focus:outline-none"
                            />
                        </div>
                        <label className="mt-3 flex cursor-pointer items-start justify-center gap-2.5 text-[11px] leading-relaxed text-[#e0aaff]/60">
                            <input
                                type="checkbox"
                                checked={optIn}
                                onChange={(e) => setOptIn(e.target.checked)}
                                data-testid="oracle-optin-checkbox"
                                className="mt-0.5 accent-[#c77dff]"
                            />
                            Keep me close — Mara may whisper to me about openings, rituals and readings.
                        </label>
                        <button type="submit" data-testid="oracle-begin-button" className="btn-glow mt-4 w-full">
                            APPROACH THE MIRROR
                        </button>
                    </form>
                ) : (
                    <>
                        <p data-testid="oracle-remaining" className="mb-4 font-cinzel text-[10px] tracking-[0.3em] text-[#ffd700]/80">
                            {locked ? "THE MIRROR HAS SPOKEN ITS THREE" : `THE MIRROR WILL ANSWER ${remaining} MORE`}
                        </p>
                        <div className="mb-4 flex flex-wrap justify-center gap-2">
                            {LONGINGS.map((l) => (
                                <button
                                    key={l}
                                    type="button"
                                    disabled={gazing || locked}
                                    onClick={() => ask(l)}
                                    data-testid={`oracle-longing-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="rounded-full border border-purple-500/25 px-4 py-2 font-cinzel text-[10px] tracking-[0.15em] text-[#e0aaff]/60 transition-colors hover:border-[#c77dff]/50 hover:text-[#e0aaff] disabled:opacity-40"
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && ask()}
                                disabled={gazing || locked}
                                data-testid="oracle-question-input"
                                placeholder={locked ? "The mirror has spoken its three…" : "Whisper your question to the mirror…"}
                                className="flex-1 rounded-full border border-purple-500/30 bg-[#18002e]/60 px-6 py-4 font-cormorant text-lg italic text-[#f8f7ff] placeholder:text-[#c77dff]/40 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:shadow-[0_0_25px_rgba(199,125,255,0.3)] focus:outline-none disabled:opacity-50"
                            />
                            <button
                                type="button"
                                onClick={() => ask()}
                                disabled={gazing || locked || !question.trim()}
                                data-testid="oracle-ask-button"
                                className="btn-glow inline-flex shrink-0 items-center gap-2 !px-6 disabled:opacity-60"
                            >
                                {gazing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                                {gazing ? "GAZING…" : "ASK"}
                            </button>
                        </div>

                        <AnimatePresence>
                            {locked && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    data-testid="oracle-locked-panel"
                                    className="glass-card mt-6 border-[#ffd700]/30 p-6"
                                >
                                    <Sparkles className="mx-auto h-5 w-5 text-[#ffd700]/80" />
                                    <p className="mt-3 font-cormorant text-lg italic leading-relaxed text-[#f8f7ff]/90">
                                        The mirror will show no more for free. A full reading with Mara opens the whole
                                        crossing — every thread, every reversal, every way back.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
                                        data-testid="oracle-book-cta"
                                        className="btn-glow mt-5"
                                    >
                                        BOOK A FULL READING
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.2, duration: 1 }}
                className="relative mt-10 flex flex-col items-center gap-5"
            >
                <button type="button" onClick={onBegin} data-testid="hero-cta-button" className="btn-ghost-glow">
                    BEGIN MY FREE RECOGNITION
                </button>
                <p data-testid="hero-tagline" className="flex items-center gap-2 font-cormorant text-base italic text-[#c77dff]/70">
                    <MoonStar className="h-4 w-4" />
                    Not every mirror holds a soul.
                </p>
                {readings != null && (
                    <p data-testid="readings-counter" className="font-cinzel text-[11px] tracking-[0.25em] text-[#e0aaff]/60">
                        <span className="font-mono text-sm tracking-normal text-[#e0aaff]">{readings.toLocaleString()}</span>{" "}
                        READINGS COMPLETED ACROSS THE MIRROR
                    </p>
                )}
            </motion.div>
        </section>
    );
};
